import { useCallback, useContext, useState } from "react"
import clsx from "clsx"
import Link from "next/link"
import useSWR from "swr"
import CreatorMyPageCategoryTopLayout from "src/components/common/layouts/CreatorMyPageCategoryTopLayout"
import TieupImg from "src/components/common/TieupImg"
import { usePseudoLoadMore } from "src/components/hooks/usePseudoLoadMore"
import DefaultLayout from "src/components/common/layouts/DefaultLayout"
import HTMLHead from "src/components/utils/HTMLHead"
import routes from "routes"
import { globalStoreContext } from "src/store/GlobalStore"
import TimeUtil from "src/utils/TimeUtil"
import { usePermission } from "src/components/hooks/usePermission"
import { fetchOwnRequestedTieups } from "../../../../models/tieup/fetchOwnRequestedTieups"
import { RequestedTieup } from "../../../../models/tieup/RequestedTieup"

/**
 * apply: 送信済み
 * applied: 受信トレイ
 */
type ApplyType = "apply" | "applied"

const LIMIT = 3

const TieupRequests: React.FC = () => {
  usePermission([2])
  const [applyType, setApplyType] = useState<ApplyType>("applied")
  const {
    globalStore: {
      user: { apiUserData },
    },
  } = useContext(globalStoreContext)

  const { data: tieups = [] } = useSWR(
    "/v1/mypage/tieup",
    async () => {
      return fetchOwnRequestedTieups().then((v) => v.items)
    },
    {
      revalidateOnFocus: false,
    },
  )

  const predicate = useCallback(
    (tieup: RequestedTieup) => {
      return applyType === "apply"
        ? tieup.userId === apiUserData?.id
        : tieup.appliedUserId === apiUserData?.id
    },
    [apiUserData, applyType],
  )

  const {
    data: limitedTieups,
    hasNext,
    setButtonElement,
    onLoadMore,
  } = usePseudoLoadMore(tieups, LIMIT, predicate)

  if (!apiUserData) return null

  return (
    <DefaultLayout isCreatorMyPage>
      <HTMLHead path="creator/mypage/tieup-requests" />
      <CreatorMyPageCategoryTopLayout navActive="requests">
        <h2 className="p-creatorMypage-title">たいあっぷ申請一覧</h2>
        <ul className="creatorMypage-tieupRequest-nav js-tab-nav">
          <li>
            <a
              className={clsx({
                "creatorMypage-tieupRequest-nav-anchor": true,
                "is-active": applyType === "applied",
              })}
              href="#contents01"
              onClick={(e) => {
                e.preventDefault()
                setApplyType("applied")
              }}
            >
              受信トレイ
            </a>
          </li>
          <li>
            <a
              className={clsx({
                "creatorMypage-tieupRequest-nav-anchor": true,
                "is-active": applyType === "apply",
              })}
              href="#contents02"
              onClick={(e) => {
                e.preventDefault()
                setApplyType("apply")
              }}
            >
              送信済み
            </a>
          </li>
        </ul>
        <div className="creatorMypage-tieupRequest-list">
          {limitedTieups.map((t) => {
            const collaborator = t.collaborator(apiUserData)

            return (
              <div key={t.id} className="creatorMypage-tieupRequest-item">
                <div className="creatorMypage-tieupRequest-profile">
                  <div className="creatorMypage-tieupRequest-image">
                    <TieupImg
                      src={collaborator?.icon_url || "/img/user-icon.png"}
                      alt={collaborator?.name || "削除済みユーザー"}
                    />
                  </div>
                  <div className="creatorMypage-tieupRequest-detail">
                    <time className="creatorMypage-tieupRequest-date">
                      {TimeUtil.formatTime(t.createdAt, "yyyy.MM.dd HH:mm")}
                    </time>
                    <p className="creatorMypage-tieupRequest-name">
                      {collaborator?.name || "削除済みユーザー"}
                    </p>
                    {/* 小説 */}
                    {t.hasBaseNovel && (
                      <div className="creatorMypage-tieupRequest-work">
                        <p className="creatorMypage-tieupRequest-work-label">
                          申請作品：
                        </p>
                        <p className="creatorMypage-tieupRequest-work-title">
                          {t.baseNovelTitle}
                        </p>
                      </div>
                    )}
                    {/* イラスト */}
                    {t.hasIllustration && (
                      <div className="creatorMypage-tieupRequest-work">
                        <p className="creatorMypage-tieupRequest-work-label">
                          申請作品：
                        </p>
                        <p className="creatorMypage-tieupRequest-work-title">
                          {t.illustrationTitle}
                        </p>
                      </div>
                    )}
                    {/* ゼロから作る場合 */}
                    {!t.hasIllustration && !t.hasBaseNovel && (
                      <div className="creatorMypage-tieupRequest-work">
                        <p className="creatorMypage-tieupRequest-work-label">
                          ゼロから一緒に作る
                        </p>
                      </div>
                    )}
                    <div className="creatorMypage-tieupRequest-action01 p-button-row">
                      <div className="p-button-col">
                        <Link
                          href={routes.creatorMyPageTieupRequestMessage(t.id)}
                        >
                          <a className="c-button-secondary -outline -full -spacing-sm">
                            申請メッセージを見る
                          </a>
                        </Link>
                      </div>
                      {collaborator && (
                        <div className="p-button-col">
                          <Link href={routes.chatRoom(t.chatId)}>
                            <a className="c-button-secondary -full">
                              チャットで連絡
                            </a>
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
          {hasNext && (
            <div className="p-button">
              <button
                className="c-button-secondary -outline -full"
                ref={setButtonElement}
                onClick={onLoadMore}
              >
                もっと見る
              </button>
            </div>
          )}
        </div>
      </CreatorMyPageCategoryTopLayout>
    </DefaultLayout>
  )
}

export default TieupRequests
